import React from "react";
import { Link, Route } from "react-router-dom";

import LeagueLoader from "@Leagues/loaders/leagueLoader";
import PlayoffLoader from "@Leagues/loaders/playoffLoader";
import ScheduleLoader from "@Leagues/loaders/scheduleLoader";

import CreateGame from "../components/CreateGame";
import CreateGameClubhouseLoader from "../components/CreateGame/loaders/createGameClubhouseLoader";
import NotFound from "../components/NotFound";
import RequireAuth from "../components/RequireAuth";
import RescheduleGame from "../components/RescheduleGame";
import RescheduleGameClubhouseLoader from "../components/RescheduleGame/loaders/rescheduleGameClubhouseLoader";
import { AUTH_LEVELS } from "../lib/globals";
import { loader as ClubhouseTeamLoader } from "../loaders/ClubhouseTeamLoader";
import ClubhouseCOI from "../pages/Clubhouse/components/COI";
import ClubhouseCOILoader from "../pages/Clubhouse/components/COI/loaders/coiLoader";
import GameSummary from "../pages/Clubhouse/components/GameSummary";
import GameSummaryLoader from "../pages/Clubhouse/components/GameSummary/loaders/gameSummaryClubhouseLoader";
import ClubhouseIndex from "../pages/Clubhouse/components/Index";
import ClubhouseLoader from "../pages/Clubhouse/components/Index/loaders/indexLoader";
import ClubhouseLeagueInfo from "../pages/Clubhouse/components/LeagueInfo";
import ClubhouseLeagueLoader from "../pages/Clubhouse/components/LeagueInfo/loaders/leagueInfoLoader";
import ClubhouseLookingForPlayers from "../pages/Clubhouse/components/LookingForPlayers";
import LFPLoader from "../pages/Clubhouse/components/LookingForPlayers/loaders/lookingForPlayersLoader";
import PitchCounts from "../pages/Clubhouse/components/PitchCounts";
import PitchCountsLoader from "../pages/Clubhouse/components/PitchCounts/loaders/pitchCountsLoader";
import ClubhousePlacement from "../pages/Clubhouse/components/Placement";
import ClubhousePlacementLoader from "../pages/Clubhouse/components/Placement/loaders/placementLoader";
import PolicyProcedures from "../pages/Clubhouse/components/PolicyProcedures";
import PolicyLoader from "../pages/Clubhouse/components/PolicyProcedures/loaders/policyProceduresLoader";
import Ratings from "../pages/Clubhouse/components/Ratings";
import RatingsLoader from "../pages/Clubhouse/components/Ratings/loaders/ratingsLoader";
import Resume from "../pages/Clubhouse/components/Resume";
import ResumeLoader from "../pages/Clubhouse/components/Resume/loaders/resumeLoader";
import Roster from "../pages/Clubhouse/components/Roster";
import Player from "../pages/Clubhouse/components/Roster/components/Player";
import PlayerLoader from "../pages/Clubhouse/components/Roster/loaders/playerLoader";
import RosterLoader from "../pages/Clubhouse/components/Roster/loaders/rosterLoader";
import ClubhouseTeamSchedule from "../pages/Clubhouse/components/Schedule";
import ClubhouseTeamScheduleLoader from "../pages/Clubhouse/components/Schedule/loaders/scheduleLoader";
import ClubhouseTeam from "../pages/Clubhouse/components/Team";
import ClubhouseTryouts from "../pages/Clubhouse/components/Tryouts";
import TryoutsLoader from "../pages/Clubhouse/components/Tryouts/loaders/tryoutsLoader";
import ClubhouseLayout from "../pages/Clubhouse/layouts/Layout";
import Rules, { loader as RulesLoader } from "../pages/Index/Rules";
import GameLogs, {
    loader as GameLogsLoader,
} from "../pages/LeagueManagement/GameLogs";
import League from "../pages/Leagues/components/League";
import PlayoffBracket from "../pages/Leagues/components/PlayoffBracket";
import LeagueSchedule from "../pages/Leagues/components/Schedule";
import { IGame, ILeague, ILeagueSchedule, IPlayer, ITeam } from "../types/api";

export default (
    <Route element={<RequireAuth allowedUserLevels={AUTH_LEVELS.clubhouse} />}>
        <Route
            path="coachs-clubhouse"
            handle={{
                crumb: () => (
                    <Link to="/coachs-clubhouse">Coach's Clubhouse</Link>
                ),
            }}
            element={<ClubhouseLayout />}
        >
            <Route
                index
                element={<ClubhouseIndex />}
                loader={ClubhouseLoader}
                handle={{
                    header: () => "Coach's Clubhouse",
                }}
            />
            <Route path="team" id="clubhouseTeam">
                <Route
                    path=":teamId"
                    element={<ClubhouseTeam />}
                    loader={ClubhouseTeamLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: ({ teamId }: { teamId: string }) => {
                            return (
                                <Link to={`/coachs-clubhouse/team/${teamId}`}>
                                    Team
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                >
                    <Route
                        path="resume"
                        element={<Resume />}
                        loader={ResumeLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/resume`}
                                    >
                                        Resume
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="looking-for-players"
                        element={<ClubhouseLookingForPlayers />}
                        loader={LFPLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/looking-for-players`}
                                    >
                                        Looking for Players
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="tryouts"
                        element={<ClubhouseTryouts />}
                        loader={TryoutsLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/tryouts`}
                                    >
                                        Post Tryout
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="policy-procedures"
                        element={<PolicyProcedures />}
                        loader={PolicyLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/policy-procedures`}
                                    >
                                        Policy &amp; Procedures
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="league-info"
                        element={<ClubhouseLeagueInfo />}
                        loader={ClubhouseLeagueLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/league-info`}
                                    >
                                        League Info
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="coi"
                        element={<ClubhouseCOI />}
                        loader={ClubhouseCOILoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/coi`}
                                    >
                                        Team Certificate
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="ratings"
                        element={<Ratings />}
                        loader={RatingsLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/ratings`}
                                    >
                                        Team Ratings
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="schedule"
                        element={<ClubhouseTeamSchedule />}
                        loader={ClubhouseTeamScheduleLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/schedule`}
                                    >
                                        Team Schedule
                                    </Link>
                                );
                            },
                        }}
                    >
                        <Route
                            path="create-game"
                            element={<CreateGame />}
                            loader={CreateGameClubhouseLoader}
                            handle={{
                                crumb: ({
                                    teamId,
                                }: {
                                    teamId: ITeam["id"];
                                }) => {
                                    return (
                                        <Link
                                            to={`/coachs-clubhouse/team/${teamId}/create-game`}
                                        >
                                            Create Game
                                        </Link>
                                    );
                                },
                            }}
                        />
                        <Route
                            path="reschedule-game/:gameId"
                            element={<RescheduleGame />}
                            loader={RescheduleGameClubhouseLoader}
                            handle={{
                                crumb: ({
                                    teamId,
                                    gameId,
                                }: {
                                    teamId: ITeam["id"];
                                    gameId: IGame["id"];
                                }) => {
                                    return (
                                        <Link
                                            to={`/coachs-clubhouse/team/${teamId}/reschedule-game/${gameId}`}
                                        >
                                            Reschedule Game
                                        </Link>
                                    );
                                },
                            }}
                        />
                        <Route
                            path="game/:gameId/logs"
                            element={
                                <RequireAuth
                                    allowedUserLevels={
                                        AUTH_LEVELS.leaguemanagement
                                    }
                                />
                            }
                        >
                            <Route
                                index
                                element={<GameLogs />}
                                loader={GameLogsLoader}
                                handle={{
                                    crumb: ({
                                        teamId,
                                        gameId,
                                    }: {
                                        teamId: ITeam["id"];
                                        gameId: IGame["id"];
                                    }) => {
                                        return (
                                            <Link
                                                to={`/coachs-clubhouse/teams/${teamId}/game/logs/${gameId}`}
                                            >
                                                Game Logs
                                            </Link>
                                        );
                                    },
                                }}
                            />
                        </Route>
                        <Route
                            path="game/:gameId/summary"
                            element={<GameSummary />}
                            loader={GameSummaryLoader}
                            handle={{
                                crumb: ({
                                    teamId,
                                    gameId,
                                }: {
                                    teamId: ITeam["id"];
                                    gameId: IGame["id"];
                                }) => {
                                    return (
                                        <Link
                                            to={`/coachs-clubhouse/teams/${teamId}/game/${gameId}/summary`}
                                        >
                                            Game Summary
                                        </Link>
                                    );
                                },
                            }}
                        />
                    </Route>
                    <Route
                        path="pitch-counts"
                        element={<PitchCounts />}
                        loader={PitchCountsLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/pitch-counts`}
                                    >
                                        Team Pitch Counts
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="roster"
                        element={<Roster />}
                        loader={RosterLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/roster`}
                                    >
                                        Team Roster
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    >
                        <Route
                            path="edit/:playerId"
                            element={<Player />}
                            loader={PlayerLoader}
                            handle={{
                                crumb: ({
                                    teamId,
                                    playerId,
                                }: {
                                    teamId: ITeam["id"];
                                    playerId: IPlayer["id"];
                                }) => {
                                    return (
                                        <Link
                                            to={`/coachs-clubhouse/team/${teamId}/roster/edit/${playerId}`}
                                        >
                                            Edit Player
                                        </Link>
                                    );
                                },
                                header: ({ header }: { header: string }) =>
                                    header,
                            }}
                        />
                        <Route
                            path="add"
                            element={<Player />}
                            loader={PlayerLoader}
                            handle={{
                                crumb: ({
                                    teamId,
                                }: {
                                    teamId: ITeam["id"];
                                }) => {
                                    return (
                                        <Link
                                            to={`/coachs-clubhouse/team/${teamId}/roster/add`}
                                        >
                                            Add Player
                                        </Link>
                                    );
                                },
                                header: ({ header }: { header: string }) =>
                                    header,
                            }}
                        />
                    </Route>
                    <Route
                        path="placement"
                        element={<ClubhousePlacement />}
                        loader={ClubhousePlacementLoader}
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                                return (
                                    <Link
                                        to={`/coachs-clubhouse/team/${teamId}/placement`}
                                    >
                                        Team Placement
                                    </Link>
                                );
                            },
                        }}
                    />
                    <Route
                        path="rules"
                        element={<Rules />}
                        loader={params =>
                            RulesLoader({
                                ...params,
                                content: "majorybl_rules_page_content",
                            })
                        }
                        handle={{
                            crumb: ({ teamId }: { teamId: ITeam["id"] }) => (
                                <Link
                                    to={`/coachs-clubhouse/team/${teamId}/rules`}
                                >
                                    MAJOR YBL Rules
                                </Link>
                            ),
                            header: () => "MAJORYBL Rules",
                        }}
                    />
                    <Route
                        path="league/:leagueId"
                        loader={LeagueLoader}
                        handle={{
                            crumb: ({ league }: { league: ILeague }) => (
                                <>
                                    {league.ageLevel}
                                    {league.designation
                                        ? " " + league.designation
                                        : ""}
                                    {league.direction
                                        ? " " + league.direction
                                        : ""}
                                </>
                            ),
                        }}
                    >
                        <Route
                            index
                            element={<League />}
                            loader={LeagueLoader}
                            handle={{
                                header: ({ header }: { header: string }) =>
                                    header,
                            }}
                        />
                        <Route
                            path="schedule"
                            element={<LeagueSchedule scheduleType="league" />}
                            loader={ScheduleLoader}
                            handle={{
                                crumb: ({
                                    schedule,
                                }: {
                                    schedule: ILeagueSchedule;
                                }) => (
                                    <Link
                                        to={`/coachs-clubhouse/team/:teamId/league/${schedule.league}/schedule`}
                                    >
                                        League Schedule
                                    </Link>
                                ),
                                header: ({ header }: { header: string }) =>
                                    header,
                            }}
                        />
                        <Route
                            path="playoffs"
                            element={<PlayoffBracket />}
                            loader={PlayoffLoader}
                            handle={{
                                crumb: (league: ILeague) => (
                                    <Link
                                        to={`/coachs-clubhouse/team/:teamId/league/${league.id}/playoffs`}
                                    >
                                        Playoff Bracket
                                    </Link>
                                ),
                                header: ({ header }: { header: string }) =>
                                    header,
                            }}
                        />
                    </Route>
                </Route>
            </Route>
            <Route
                path="rules"
                element={<Rules />}
                loader={params =>
                    RulesLoader({
                        ...params,
                        content: "majorybl_rules_page_content",
                    })
                }
                handle={{
                    crumb: () => (
                        <Link to="/coachs-clubhouse/rules">
                            MAJOR YBL Rules
                        </Link>
                    ),
                    header: () => "MAJORYBL Rules",
                }}
            />
        </Route>
        <Route
            path="*"
            element={<NotFound parent="clubhouse" />}
            handle={{ header: () => "Clubhouse Page Not Found" }}
        />
    </Route>
);
